<template>
  <div class="modal fade" id="registrationModal" tabindex="-1" aria-labelledby="registrationModalTitle">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="registrationModalTitle">
            New User Request
          </h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          
          <p class="text-muted mb-4 small">
            Please tell us how to contact you and a bit about your NTN project.
          </p>

          <form class="card-body">
            
            <div class="col d-flex align-items-center mb-3">
              <label for="inputName" class="col-sm-4 form-label small fw-bold me-3">
                Name
                <span class="text-danger">*</span>
              </label>
              <input 
                id="inputName"
                type="text" 
                class="form-control form-control-sm" 
                placeholder="Jo Doe" 
                v-tooltip 
                title="Please enter your full name"
                required
                v-model="state.userData.name" 
              />
            </div>
            
            <div class="col d-flex align-items-center mb-3">
              <label for="inputEmail" class="col-sm-4 form-label small fw-bold me-3">
                Email
                <span class="text-danger">*</span>
              </label>
              <input 
                id="inputEmail"
                type="text" 
                class="form-control form-control-sm" 
                pattern="^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$"
                v-tooltip 
                title="Please enter a valid email address (e.g. name@example.com)"
                placeholder="jo.doe@example.com" 
                required
                v-model="state.userData.email" 
              />
            </div>
            
            <div v-if="!isFirebase" class="col d-flex align-items-center mb-3">
              <label for="inputAuth" class="col-sm-4 form-label small fw-bold me-5">
                Authentication
                <span class="text-danger">*</span>
              </label>
              <select 
                id="inputAuth" 
                class="form-select form-select-sm" 
                v-model="state.userData.authProvider" 
                v-tooltip title="Please tell us how you want to login" 
              >
                <option v-for="(provider, index) in authProviders" :key="index" :value="provider">
                  {{ provider }}
                </option>
              </select>
            </div>

            <div class="col d-flex align-items-center mb-3">
              <label for="inputCompany" class="col-sm-4 form-label small fw-bold me-3">
                Company Name
                <span class="text-danger">*</span>
              </label>
              <input 
                id="inputCompany"
                type="text" 
                class="form-control form-control-sm" 
                placeholder="ACME Inc." 
                v-tooltip 
                title="Please enter the name of your company/organization"
                required
                v-model="state.userData.orgName" 
              />
            </div>
            
            <div class="col d-flex align-items-center mb-3">
              <label for="inputUrl" class="col-sm-4 form-label small fw-bold me-3">
                Company Website
                <span class="text-danger">*</span>
              </label>
              <input 
                id="inputUrl"
                type="text" 
                class="form-control form-control-sm" 
                placeholder="https://www.acme.com" 
                pattern="https?://.+" 
                v-tooltip 
                title="Please enter the URL of your company/organization website"
                required
                v-model="state.userData.orgUrl" 
              />
            </div>
            
            <div class="col d-flex align-items-center mb-3">
              <label for="inputRole" class="col-sm-4 form-label small fw-bold me-5">
                Role
              </label>
              <select 
                id="inputRole" 
                class="form-select form-select-sm" 
                v-model="state.userData.orgRole" 
                v-tooltip title="Please tell us your role in the company/organization" 
              >
                <option v-for="(role, index) in orgRoles" :key="index" :value="role">
                  {{ role }}
                </option>
              </select>
            </div>

            <div class="col d-flex align-items-center mb-3">
              <label for="inputCountry" class="col-sm-4 form-label small fw-bold me-3">
                Country
                <span class="text-danger">*</span>
              </label>
              <input 
                id="inputCountry"
                type="text" 
                class="form-control form-control-sm" 
                placeholder="USA" 
                v-tooltip 
                title="Please enter the country where you will use NTN service" 
                required
                v-model="state.userData.country" 
              />
            </div>
            
            <div class="col d-flex align-items-center mb-3">
              <label for="inputProject" class="col-sm-4 form-label small fw-bold me-3">
                Project Overview
                <span class="text-danger">*</span>
              </label>
              <input 
                id="inputProject"
                type="text" 
                class="form-control form-control-sm" 
                placeholder="Soil probe" 
                v-tooltip 
                title="Please tell us a bit about your project or product" 
                required
                v-model="state.userData.projectDescription" 
              />
            </div>
            
            <div class="col d-flex align-items-center mb-3">
              <label for="inputModule" class="col-sm-4 form-label small fw-bold me-3">
                Module
              </label>
              <input 
                id="inputModule"
                type="text" 
                class="form-control form-control-sm" 
                :placeholder="placeholderModule" 
                v-tooltip 
                title="Please tell us which module/device your product is using" 
                v-model="state.userData.module" 
              />
            </div>            

          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
          <button type="button" class="btn btn-primary" @click="saveObj">Submit</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, onMounted } from 'vue';
import { Modal } from 'bootstrap';
import { ApiEndpoint, isFirebase } from '@/utils/config';
import { apiFetch } from '@/utils/api';

const emit = defineEmits(['formDataSubmitted', 'formClosed']);

const state = reactive({
  modalMounted: null,
  userData: {
    name: null,
    email: null,
    orgName: null,
    orgUrl: null,
    orgRole: null,
    projectDescription: null,
    country: null,
    module: null,
    authProvider: null,
    // requestId: null,
  },
});

const authProviders = [
  'google',
  'github',
  'username/password',
];

const orgRoles = [
  'Engineer / Developer',
  'Engineering Manager',
  'Executive / Founder',
  'Researcher',
];

const modules = [
  'Nordic nRF9151',
  'Murata Type1SC',
  'Quectel CC660D-LS',
  'Quectel BG95-S5',
  'Quectel BG770-SN',
  'Semtech HL78xx',
  'other',
];

const placeholderModule = computed(() => {
  const randomIndex = Math.floor(Math.random() * (modules.length - 1));
  return modules[randomIndex];
});

const registrationUrl = `${ApiEndpoint.USERS}/register`;

const openModal = () => {
  state.modalMounted.show();
};

const closeModal = () => {
  state.modalMounted.hide();
  emit('formClosed');
};

const saveObj = async () => {
  state.modalMounted.hide();
  // TODO: form validation
  const result = await apiFetch(registrationUrl, {
    method: 'POST',
    body: state.userData,
  });
  state.userData.requestId = result.requestId;
  emit('formDataSubmitted', state.userData);
};

onMounted(() => {
  state.modalMounted = new Modal(`#registrationModal`, {});
  state.userData.orgRole = orgRoles[0];
  if (isFirebase) state.userData.authProvider = 'google';
  openModal();
});

</script>